import { useEffect, useState } from 'react';
import { getUrl } from 'aws-amplify/storage';
import { amplifyConfigured } from '@/lib/amplify-ready';

const STORAGE_BUCKET = 'shopgndMedia';
const PUBLIC_MEDIA_CACHE = 'public, max-age=31536000, immutable';
/** 7 days — Amplify presign max; pairs with ResponseCacheControl for browser caching. */
const MEDIA_URL_EXPIRES_SEC = 7 * 24 * 60 * 60;

const STORAGE_PREFIXES = [
  'products',
  'classifieds',
  'vendors',
  'avatars',
  'advertise-inquiries',
] as const;

/** S3 object key stored in the database (not a presigned URL). */
export function isStorageObjectPath(value: string): boolean {
  return value.startsWith('public/');
}

/** Amplify/S3 presigned URL — pathname must not be rewritten without re-signing. */
export function isPresignedS3Url(url: string): boolean {
  try {
    const u = new URL(url);
    if (!u.hostname.includes('amazonaws.com')) return false;
    return u.search.includes('x-id=') || u.search.includes('X-Amz-Algorithm=');
  } catch {
    return false;
  }
}

/** Extract `public/...` from a legacy full S3 or presigned URL, if possible. */
export function storagePathFromUrl(maybeUrl: string): string | null {
  if (isStorageObjectPath(maybeUrl)) return maybeUrl;
  try {
    const u = new URL(maybeUrl);
    const path = decodeURIComponent(u.pathname.replace(/^\//, ''));
    if (path.startsWith('public/')) return path;
  } catch {
    /* not a URL */
  }
  return null;
}

/** Normalize DynamoDB values to durable `public/...` paths when possible. */
export function normalizeStoragePath(value: string): string {
  const trimmed = value.trim();
  const fromUrl = storagePathFromUrl(trimmed);
  if (fromUrl) return fromUrl;
  if (isStorageObjectPath(trimmed)) return trimmed;
  for (const prefix of STORAGE_PREFIXES) {
    if (trimmed.startsWith(`${prefix}/`)) return `public/${trimmed}`;
  }
  return trimmed;
}

/** Values safe to pass to `<img src>` or resolve via Storage (avoids `/hub`-style 404s). */
export function isResolvableMediaRef(value: string): boolean {
  const v = normalizeStoragePath(value);
  if (!v) return false;
  if (v.startsWith('data:') || v.startsWith('http://') || v.startsWith('https://')) return true;
  return isStorageObjectPath(v);
}

/** Persist paths in DynamoDB; keep external https/data URLs as-is. */
export function normalizeStoredMediaUrl(urlOrPath: string): string {
  return normalizeStoragePath(urlOrPath);
}

async function presignStoragePath(path: string): Promise<string | undefined> {
  try {
    await amplifyConfigured;
    const { url } = await getUrl({
      path,
      options: {
        bucket: STORAGE_BUCKET,
        expiresIn: MEDIA_URL_EXPIRES_SEC,
        cacheControl: PUBLIC_MEDIA_CACHE,
      },
    });
    return url.toString();
  } catch {
    return undefined;
  }
}

export async function resolveMediaUrl(stored: string): Promise<string | undefined> {
  const normalized = normalizeStoragePath(stored);
  if (!normalized) return undefined;

  if (normalized.startsWith('data:')) return normalized;

  if (isStorageObjectPath(normalized)) {
    return presignStoragePath(normalized);
  }

  if (normalized.startsWith('http://') || normalized.startsWith('https://')) {
    return normalized;
  }

  return undefined;
}

/** Alias used by property listing hydration. */
export const getMediaUrl = resolveMediaUrl;

/** Fresh presigned (or public) URL for display; paths resolve on change. */
export function useResolvedMediaUrl(stored?: string): string | undefined {
  const [src, setSrc] = useState<string | undefined>(() => {
    if (!stored || !isResolvableMediaRef(stored)) return undefined;
    const path = normalizeStoragePath(stored);
    if (isStorageObjectPath(path)) return undefined;
    return path;
  });

  useEffect(() => {
    if (!stored || !isResolvableMediaRef(stored)) {
      setSrc(undefined);
      return;
    }
    const path = normalizeStoragePath(stored);
    if (!isStorageObjectPath(path)) {
      setSrc(path);
      return;
    }
    let cancelled = false;
    void presignStoragePath(path)
      .then((url) => {
        if (!cancelled) setSrc(url);
      })
      .catch(() => {
        if (!cancelled) setSrc(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [stored]);

  return src;
}
